import React from "react";
import { Grid, TextField, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import ReadMoreReadLess from "../../app/common/ReadMoreReadLess";
import { useAppSelector } from "../../app/store/configureStore";
import { AnswersProps } from "../../app/models/Answers";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

const schema = yup.object().shape({
  safeEnv: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  healthEnv: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
  learningEnv: yup
    .string()
    .required("Must give an answer")
    .max(500, "Max characters reached"),
});

const StatementSafeEnvironment: React.FC<AnswersProps> = ({
  answersData,
  onAnswerChange,
}) => {
  const defaultValues = {
    safeEnv: answersData.find((answer) => answer.systemCodeId === 15)?.answer,
    healthEnv: answersData.find((answer) => answer.systemCodeId === 16)?.answer,
    learningEnv: answersData.find((answer) => answer.systemCodeId === 17)
      ?.answer,
  };
  const {
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  const { user } = useAppSelector((state) => state.account);

  const renderSafetyContent = () => {
    if (user?.credentialTypeId !== 4) {
      return (
        <>
          <li>
            What policies and procedures do you use to ensure the indoor and
            outdoor learning environments are checked daily for safety hazards?
          </li>
          <li>How do you maintain the approved staff-to-child ratio?</li>
          <li>
            How do you handle transitions and tracking of children throughout
            the day?
          </li>
          <li>
            How are hazardous materials stored and kept out of reach of
            children?
          </li>
        </>
      );
    } else {
      return (
        <>
          <li>
            How do you assist parents with modifications within the home to
            ensure the safety of children with different abilities?
          </li>
          <li>
            What resources do you provide to inform parents of potential
            hazardous materials within their home, vehicles, and outdoor area?
          </li>
          <li>
            How do you partner with families to make their home safe for
            children at different stages of development?
          </li>
          <li>
            What processes do you implement to ensure families have well-planned
            and well-organized emergency procedures and supplies?
          </li>
          <li>
            How do you assist parents with modifying environments to ensure
            child-accessible areas are free from harmful conditions and
            materials?
          </li>
        </>
      );
    }
  };

  const renderHealthContent = () => {
    if (user?.credentialTypeId !== 4) {
      return (
        <>
          <li>
            What steps do you take to ensure the children's bathroom and diaper
            area are cleaned and maintained throughout the day?
          </li>
          <li>
            What procedures are in place to make sure sufficient cleaning and
            sanitizing supplies are available throughout the day?
          </li>
          <li>
            How do you keep the food prep area and classroom surfaces clean and
            sanitized?
          </li>
          <li>
            How are handwashing policies and procedures executed throughout the
            day?
          </li>
        </>
      );
    } else {
      return (
        <>
          <li>
            How do you connect families to community groups and resources to
            support medical, mental health, dental, and nutritional needs?
          </li>
          <li>
            What practices do you implement to educate parents on proper hand
            washing procedures?
          </li>
          <li>
            How do you help parents to ensure toys, materials, and surfaces are
            appropriately cleaned and sanitized within the home?
          </li>
          <li>
            What techniques do you use to help parents understand the need for
            exercise and rest for themselves and their children?
          </li>
          <li>
            How do you encourage parents to prepare nutritious foods for snacks
            and meals?
          </li>
        </>
      );
    }
  };

  const renderLearningContent = () => {
    if (user?.credentialTypeId !== 4) {
      return (
        <>
          <li>
            How do you ensure children have choices to explore different
            learning areas in the classroom?
          </li>
          <li>
            What strategies do you use to plan activities that fulfill
            children's interests?
          </li>
          <li>
            How do you ensure activities and materials meet the needs of all
            children in your program?
          </li>
          <li>
            What steps do you take to make sure learning areas have
            well-arranged space for children to play and explore?
          </li>
        </>
      );
    } else {
      return (
        <>
          <li>
            How do you support relationships and positive interactions between
            family members?
          </li>
          <li>
            How do you help parents introduce a variety of materials and
            opportunities for learning based on their child's developmental
            levels, abilities, and interests?
          </li>
          <li>
            What techniques do you use to help parents select and
            promote/facilitate the use of materials that are engaging and
            challenging?
          </li>
          <li>
            How do you help parents establish simple and consistent routines?
          </li>
          <li>
            How do you encourage parents to incorporate background, family
            structure, culture, and personal values when preparing, arranging,
            and changing their home environment?
          </li>
        </>
      );
    }
  };

  return (
    <>
      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Safe Environment
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do you keep children safe indoors and out?"
            : "How do you teach families to keep children safe indoor and out?"}
        </Typography>
        <ReadMoreReadLess>{renderSafetyContent()}</ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label="My teaching practices meet the standard for a safe learning environment because"
          multiline
          rows={2}
          fullWidth
          {...register("safeEnv")}
          name="safeEnv"
          value={
            answersData.find((answer) => answer.systemCodeId === 15)?.answer ||
            ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = 15;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.safeEnv?.message}
          helperText={errors.safeEnv?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.safeEnv?.length}/500
        </Typography>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <Typography variant="body1" fontWeight="bold" paragraph>
          Healthy Environment
        </Typography>
        <Typography variant="body1" paragraph>
          {user?.credentialTypeId !== 4
            ? "How do you create a healthy learning environment?"
            : "How do you help/teach families to create a healthy learning environment in their homes?"}
        </Typography>
        <ReadMoreReadLess>{renderHealthContent()}</ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <TextField
          label="My teaching practices meet the standard for a healthy learning environment because"
          multiline
          rows={2}
          fullWidth
          {...register("healthEnv")}
          name="healthEnv"
          value={
            answersData.find((answer) => answer.systemCodeId === 16)?.answer ||
            ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = 16;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.healthEnv?.message}
          helperText={errors.healthEnv?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.healthEnv?.length}/500
        </Typography>
      </Grid>
      <Grid item xs={12} px={3} pt={3}>
        <Typography fontWeight={700} paragraph>
          Learning Environment
        </Typography>
        <Typography>
          {user?.credentialTypeId === 4
            ? "How do you help families make sure children have a learning environment that encourages interaction, play, engagement, and exploration?"
            : "How do you make sure children have a learning environment that encourages interaction, play, engagement, and exploration?"}
        </Typography>
        <ReadMoreReadLess>{renderLearningContent()}</ReadMoreReadLess>
      </Grid>
      <Grid item xs={12} px={3} py={3}>
        <TextField
          label="My teaching practices meet the standard for a learning environment because"
          multiline
          rows={2}
          fullWidth
          {...register("learningEnv")}
          name="learningEnv"
          value={
            answersData.find((answer) => answer.systemCodeId === 17)?.answer ||
            ""
          }
          onChange={(e) => {
            const newValue = e.target.value;
            const systemCodeId = 17;

            newValue.length <= 500 && onAnswerChange(systemCodeId, newValue);
          }}
          error={!!errors.learningEnv?.message}
          helperText={errors.learningEnv?.message}
        />
        <Typography fontSize={13}>
          {defaultValues.learningEnv?.length}/500
        </Typography>
      </Grid>
    </>
  );
};
export default StatementSafeEnvironment;
